import moment from "moment";
import { utils as xlsxUtils } from "xlsx";

/**
 * Calendar-date helpers (no timezone shift for YYYY-MM-DD values).
 */

/**
 * YYYY-MM-DD from a Date using the browser's local calendar day.
 * Use for MUI datepicker selections — the clicked day must not be re-zoned.
 */
export function formatLocalCalendarDateYmd(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseDateOnly(value) {
  if (value == null || value === "") return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
    };
  }

  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return {
      year: Number(iso[1]),
      month: Number(iso[2]),
      day: Number(iso[3]),
    };
  }

  const m = moment(s, moment.ISO_8601, true);
  if (m.isValid()) {
    return { year: m.year(), month: m.month() + 1, day: m.date() };
  }

  const loose = moment(s);
  if (loose.isValid()) {
    return { year: loose.year(), month: loose.month() + 1, day: loose.date() };
  }

  return null;
}

export function formatDateOnlyYmd(partsOrValue) {
  const parts =
    partsOrValue && typeof partsOrValue === "object" && "year" in partsOrValue
      ? partsOrValue
      : parseDateOnly(partsOrValue);
  if (!parts) return "";
  const { year, month, day } = parts;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function formatDateOnlyDisplay(value) {
  const ymd = formatDateOnlyYmd(value);
  if (!ymd) return "";
  return moment(ymd, "YYYY-MM-DD", true).format("MMM DD yyyy");
}

export function compareDateOnly(a, b) {
  const ay = formatDateOnlyYmd(a);
  const by = formatDateOnlyYmd(b);
  if (!ay || !by) return 0;
  if (ay < by) return -1;
  if (ay > by) return 1;
  return 0;
}

export function isDateOnlyInRange(date, min, max) {
  const d = formatDateOnlyYmd(date);
  if (!d) return false;
  const minY = formatDateOnlyYmd(min);
  const maxY = formatDateOnlyYmd(max);
  if (minY && d < minY) return false;
  if (maxY && d > maxY) return false;
  return true;
}

/** Parse slash dates as calendar literals (M/D/Y or D/M/Y), no timezone shift. */
function parseSlashDateParts(s) {
  const m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (!m) return null;
  let a = Number(m[1]);
  let b = Number(m[2]);
  let year = Number(m[3]);
  if (year < 100) year += 2000;

  let month;
  let day;
  if (a > 12 && b <= 12) {
    day = a;
    month = b;
  } else if (b > 12 && a <= 12) {
    month = a;
    day = b;
  } else {
    // Ambiguous (e.g. 5/19): Excel US templates use M/D/YYYY
    month = a;
    day = b;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** Excel / CSV date cell → YYYY-MM-DD (calendar date, not UTC-shifted). */
export function parseDateFromExcel(val) {
  if (val == null || val === "") return "";

  if (typeof val === "number" && !Number.isNaN(val)) {
    const dc = xlsxUtils.parse_date_code(val);
    if (dc) {
      return formatDateOnlyYmd({
        year: dc.y,
        month: dc.m,
        day: dc.d,
      });
    }
  }

  if (val instanceof Date && !Number.isNaN(val.getTime())) {
    // SheetJS date-only cells are UTC midnight — use UTC parts, not local TZ
    return formatDateOnlyYmd({
      year: val.getUTCFullYear(),
      month: val.getUTCMonth() + 1,
      day: val.getUTCDate(),
    });
  }

  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const slashParts = parseSlashDateParts(s);
  if (slashParts) return formatDateOnlyYmd(slashParts);

  const formats = [
    "YYYY-MM-DD",
    "YYYY/MM/DD",
    "DD/MM/YYYY",
    "MM/DD/YYYY",
    "D/M/YYYY",
    "M/D/YYYY",
    "DD-MM-YYYY",
    "MM-DD-YYYY",
    "D-M-YYYY",
    "M-D-YYYY",
    "DD.MM.YYYY",
    "MM.DD.YYYY",
    "D.M.YYYY",
    "M.D.YYYY",
    "DD/MM/YY",
    "MM/DD/YY",
    "D/M/YY",
    "M/D/YY",
    "DD-MM-YY",
    "MM-DD-YY",
    "MMM D, YYYY",
    "MMM DD, YYYY",
    "D MMM YYYY",
    "DD MMM YYYY",
    "MMMM D, YYYY",
    "MMMM DD, YYYY",
    "MMM D YYYY",
    "DD MMM YYYY",
  ];

  for (const fmt of formats) {
    const m = moment.utc(s, fmt, true);
    if (m.isValid()) return m.format("YYYY-MM-DD");
  }

  const isoTry = moment.utc(s, moment.ISO_8601, true);
  if (isoTry.isValid()) return isoTry.format("YYYY-MM-DD");

  const any = moment.utc(s, [
    "YYYY-MM-DD",
    "DD/MM/YYYY",
    "MM/DD/YYYY",
    "DD-MM-YYYY",
    "MM-DD-YYYY",
  ]);
  return any.isValid() ? any.format("YYYY-MM-DD") : "";
}

function addCalendarDays(parts, days) {
  const anchor = new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day + days, 12, 0, 0),
  );
  return {
    year: anchor.getUTCFullYear(),
    month: anchor.getUTCMonth() + 1,
    day: anchor.getUTCDate(),
  };
}

/** Monday–Saturday week (timezone-neutral calendar math). */
export function getWeekRangeMonToSat(selectedDate) {
  const parts = parseDateOnly(selectedDate);
  if (!parts) return { startDate: "", endDate: "" };

  const dow = new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0),
  ).getUTCDay();
  const daysFromMonday = dow === 0 ? 6 : dow - 1;
  const monday = addCalendarDays(parts, -daysFromMonday);
  const saturday = addCalendarDays(monday, 5);

  return {
    startDate: formatDateOnlyYmd(monday),
    endDate: formatDateOnlyYmd(saturday),
  };
}

/** Saturday (end) of the Mon–Sat week that contains today. */
export function getEndOfCurrentWeekYmd(referenceDate = new Date()) {
  const { endDate } = getWeekRangeMonToSat(referenceDate);
  return endDate || "";
}

/**
 * Mon–Sat week for timesheet entry; caps endDate to the current week when the
 * selected week extends past today’s week (same cap as legacy week-wise behavior).
 */
export function getTimesheetPeriodRange(selectedDate) {
  const { startDate, endDate } = getWeekRangeMonToSat(selectedDate);
  if (!startDate || !endDate) return { startDate: "", endDate: "" };

  const weekEndCap = getEndOfCurrentWeekYmd();
  if (weekEndCap && compareDateOnly(endDate, weekEndCap) > 0) {
    return { startDate, endDate: weekEndCap };
  }
  return { startDate, endDate };
}

/**
 * First and last calendar day of the month containing selectedDate.
 * Optionally caps endDate to the end of the current week (for current-month timesheets).
 */
export function getCalendarMonthRange(
  selectedDate,
  { capEndToCurrentWeek = true } = {},
) {
  const parts = parseDateOnly(selectedDate);
  if (!parts) return { startDate: "", endDate: "" };

  const startDate = formatDateOnlyYmd({
    year: parts.year,
    month: parts.month,
    day: 1,
  });

  const lastDay = new Date(parts.year, parts.month, 0).getDate();
  let endDate = formatDateOnlyYmd({
    year: parts.year,
    month: parts.month,
    day: lastDay,
  });

  if (capEndToCurrentWeek) {
    const weekEndYmd = getEndOfCurrentWeekYmd();
    if (weekEndYmd && compareDateOnly(endDate, weekEndYmd) > 0) {
      endDate = weekEndYmd;
    }
  }

  return { startDate, endDate };
}

/** Earliest selectable timesheet period (default: 12 months ago, 1st of month). */
export function getTimesheetPeriodPickerMinDate(monthsBack = 12) {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(1);
  d.setMonth(d.getMonth() - monthsBack);
  return formatDateOnlyYmd(d);
}

export function parseYmdToTimestamp(value) {
  const ymd = formatDateOnlyYmd(value);
  if (!ymd) return null;
  const parts = parseDateOnly(ymd);
  return new Date(parts.year, parts.month - 1, parts.day).getTime();
}

/** Parse YYYY-MM-DD for MUI datepicker (no UTC off-by-one in US TZ). */
export function parseYmdToLocalDate(value) {
  const ymd = formatDateOnlyYmd(value);
  if (!ymd) return null;
  const parts = parseDateOnly(ymd);
  // Use midday to avoid any edge cases around DST at midnight while still
  // representing the intended calendar date in local time.
  return new Date(parts.year, parts.month - 1, parts.day, 12, 0, 0, 0);
}

/**
 * Parse YYYY-MM-DD for min/max bounds. Must be start-of-day so the same date
 * is selectable (MUI compares day cells at 00:00 with minDate/maxDate).
 */
export function parseYmdToLocalDateStart(value) {
  const ymd = formatDateOnlyYmd(value);
  if (!ymd) return null;
  const parts = parseDateOnly(ymd);
  return new Date(parts.year, parts.month - 1, parts.day, 0, 0, 0, 0);
}

export function timesheetOverlapsDateRange(timesheet, filterStart, filterEnd) {
  if (!filterStart && !filterEnd) return true;

  const tsStart = parseYmdToTimestamp(timesheet?.start_date);
  const tsEnd = parseYmdToTimestamp(
    timesheet?.end_date ?? timesheet?.start_date,
  );
  if (tsStart == null && tsEnd == null) return false;

  const rangeStart =
    parseYmdToTimestamp(filterStart) ?? parseYmdToTimestamp(filterEnd);
  const rangeEnd =
    parseYmdToTimestamp(filterEnd) ?? parseYmdToTimestamp(filterStart);
  const effectiveTsStart = tsStart ?? tsEnd;
  const effectiveTsEnd = tsEnd ?? tsStart;

  return effectiveTsStart <= rangeEnd && effectiveTsEnd >= rangeStart;
}
